/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC, useState, useMemo, useContext, useEffect, useCallback, useRef } from 'react';
import styles from './grid.module.scss';
import { AgGridReact } from 'ag-grid-react';
import ReactGA from 'react-ga4';
import sortDown from '../../../assets/grid/sort-down.svg';
import sortUp from '../../../assets/grid/sort-up.svg';
import unsort from '../../../assets/grid/sort-none.svg';
import filterNotFound from '../../../assets/grid/filter-notFound.svg';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import './grid.scss';
import ObrasAPI from '../../../services/APIObras';
import { useTranslation } from 'react-i18next';
import Error from '../../../components/Error/Error';
import LoadingSpinner from '../../../components/loadingSpinner/loadingSpinner';
import { useNavigate } from 'react-router-dom';
import { ColumnState, GridReadyEvent, IDatasource, RowClassParams, RowClickedEvent, SortChangedEvent, SortDirection } from 'ag-grid-community';
import { ViewContext } from '../../../context/ViewProvider';
import { FilterContext } from '../../../context/FilterProvider';
import { UserModeContext } from '../../../context/UserModeProvider';
import TagsOverlay from '../../../components/tagsOverlay/tagsOverlay';
import { DEFAULT_PROJECT_MODE } from '../../../types/Authorization';
import { FilterValue } from '../../../types/FiltersTypes';
import { CircularProgress } from '@mui/material';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface gridProps {
  children?: JSX.Element;
  enableExport: (enabled: boolean) => void;
}

interface GridRow {
  codCentro: string;
  fecAdjudicacion?: string;
  importeAct?: number | string | null;
  [key: string]: unknown;
}

interface GridResponse {
  obras?: GridRow[];
  total?: number;
}

let order = {
  colId: '',
  sort: ''
};

const cache = {
  cached: false,
  filter: {} as Record<string, FilterValue[]>,
  masterFilters: new Map<string, string[]>(),
};

const ROWS_PER_PAGE = 20;

const grid: FC<gridProps> = (props) => {
  const { enableExport } = props;
  const [t, i18n] = useTranslation();
  const navigate = useNavigate();
  const viewContext = useContext(ViewContext);
  const filterContext = useContext(FilterContext);
  const [refreshData, setRefreshData] = useState(false);
  const gridRef = useRef<AgGridReact>(null);
  const [errorData, setErrorData] = useState(false);
  const [noRows, setNoRows] = useState(false);
  const userModeContext = useContext(UserModeContext);
  const [noTags, setNoTags] = useState(true);

  const [loading, setLoading] = useState(viewContext.mapIsSelected === false);
  const [loadingMore, setLoadingMore] = useState(false);

  if (filterContext.appliedFilters !== cache.filter && (Object.keys(filterContext.appliedFilters).length > 0 || Object.keys(cache.filter).length > 0)) {
    cache.filter = filterContext.appliedFilters;
    cache.masterFilters = filterContext.masterFilters;
    cache.cached = true;
    setLoading(viewContext.mapIsSelected === false);
    setRefreshData(true);
    setTimeout(() => { setRefreshData(false); }, 100);
  }

  useEffect(() => {
    setRefreshData(true);
    setTimeout(() => { setRefreshData(false); }, 100);
  }, [i18n.language]);

  const [numberList, setNumberList] = useState(0);

  const orderSort = order.sort as SortDirection || undefined;

  const defColumn = (
    headerName: string,
    field: string,
    cellClass: string,
    flex: number,
    headerAlign?: 'left' | 'center' | 'right',
  ) => {
    return {
      headerName,
      field,
      unSortIcon: true,
      cellClass,
      headerClass: headerAlign === 'right' ? 'right' : styles.cellHeader,
      initialSort: order.colId === field ? orderSort : undefined,
      flex
    };
  };

  const columnDefs = [
    defColumn(t('homeList.code'), 'codCentro', styles.cellBold, 1.1),
    defColumn(t('homeList.nomCenter'), 'nomInternoCentro', styles.cell, 2.3),
    defColumn(t('homeList.nomPais'), 'nomPais', styles.cell, 1.0),
    defColumn(t('homeList.year'), 'fecAdjudicacion', styles.cell, 0.7),
    defColumn(t('homeList.import'), 'importeAct', styles.cellRight, 1.0, 'right'),
    defColumn(t('homeList.type'), 'tipoObra', styles.cell, 2.0),
    defColumn(t('homeList.state'), 'estadoContrato', styles.cell, 1.1),
  ];

  const processRow = (data: GridRow[]) => {
    data.map((row) => {
      const splitDate = row.fecAdjudicacion?.split('/');
      row.fecAdjudicacion = splitDate ? splitDate[splitDate.length - 1] : '-';
      row.importeAct = row.importeAct ? `${Number(row.importeAct).toLocaleString('es-ES', { maximumFractionDigits: 2, minimumFractionDigits: 2, useGrouping: true })} EUR` : '-';
    });
  };

  // never changes, so we can use useMemo
  const defaultColDef = useMemo(() => ({
    flex: 1,
    sortable: true,
  }), []);

  const icons = {
    sortAscending: '<img style="padding-top: 3px;" src=' + sortDown + ' />',
    sortDescending: '<img style="padding-top: 3px;" src=' + sortUp + ' />',
    sortUnSort: '<img style="padding-top: 3px;" src=' + unsort + ' />'
  };

  const onSortChanged = (event: SortChangedEvent<GridRow>) => {
    const sortModel = event.api.getColumnState().filter((col: ColumnState) => col.sort);
    const firstSort = sortModel[0];
    order = firstSort ? {
      colId: firstSort.colId || '',
      sort: firstSort.sort || '',
    } : {
      colId: '',
      sort: ''
    };
  };

  const handleClick = (row: RowClickedEvent<GridRow>) => {
    if (!row.data) return;
    userModeContext.handleChangeSwitch(DEFAULT_PROJECT_MODE);
    ReactGA.event('click_details', {
      event_category: 'list',
      capability: 'can_read',
      code: row.data.codCentro
    });
    navigate(`/${row.data.codCentro}/detalles`);
  };

  useEffect(() => {
    window.addEventListener('error', e => {
      if (e.message === 'ResizeObserver loop limit exceeded') {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    });
  }, []);


  const onGridReady = useCallback((params: GridReadyEvent<GridRow>) => {
    const dataSource: IDatasource = {
      rowCount: undefined,
      getRows: (params) => {
        if (params.startRow === 0) {
          setLoading(true);
          setLoadingMore(false);
        } else {
          setLoadingMore(true);
        }
        const page = Math.floor(params.startRow / ROWS_PER_PAGE) + 1;
        let query = `total=${ROWS_PER_PAGE}&pagina=${page}`;
        if (order.colId != '' && order.sort != '') {
          query = `${query}&orden=${order.sort}&ordenar_por=${order.colId}`;
        }
        const api = ObrasAPI.getInstance();
        api.getObrasListAPI(
          query,
          filterContext.transformMasterFilters(
            cache.filter,
            cache.cached ? cache.masterFilters : undefined
          )).then((data) => {
          const response = data as unknown as GridResponse;
          if (!Array.isArray(response.obras)) {
            setErrorData(true);
            setLoading(false);
            setLoadingMore(false);
            params.successCallback([], 0);
            return;
          }

          setErrorData(false);
          const total = response.total || 0;
          setNumberList(total);
          if (params.startRow === 0 && total === 0) {
            setNoRows(true);
            enableExport(false);
          } else {
            setNoRows(false);
            enableExport(true);
          }
          const { obras } = response;
          processRow(obras);
          let lastRow = -1;
          if (obras.length < ROWS_PER_PAGE) {
            lastRow = total;
          }
          params.successCallback(obras, lastRow);
          setLoading(false);
          setLoadingMore(false);
        }).catch(() => {
          setErrorData(true);
          setLoading(false);
          setLoadingMore(false);
          params.successCallback([], 0);
        });

      },
    };
    // pass the dataSource object instead of the row data
    params.api.setDatasource(dataSource);
  }, []);

  const getRowStyle = (params: RowClassParams<GridRow>) => {
    if (params.data === undefined) {
      return { opacity: 0 };
    }
    return { opacity: 1 };
  };

  useEffect(() => {
    const appliedTags = [];
    Object.keys(filterContext.appliedFilters).map((key) => {
      appliedTags.push(...filterContext.appliedFilters[key]);
    });
    setNoTags(appliedTags.length === 0);
  }, [filterContext.appliedFilters]);

  return (
    <div className={styles.grid} data-testid="grid-test">
      {
        (viewContext.mapIsSelected === false) ?
          <div className={styles.resultsNumber}>
            <div id="shownumber" className={styles.showNumber}>
              <div>{t('homeList.show')} <b>{numberList} {numberList !== 1 ? t('homeList.projects') : t('homeList.project')}</b></div>
            </div>
            {!noTags ? <div className={styles.tagsOverlay}>
              <TagsOverlay />
            </div> : null}
          </div> : false
      }
      {
        (loading) ?
          <LoadingSpinner /> : false
      }
      {
        (noRows && viewContext.mapIsSelected === false) ?
          <div className={styles.filterNotFound}>
            <img src={filterNotFound} />
            <div id="titleerror" className={styles.titleError}>{t('homeList.notFoundTitle')}</div>
            <div id="titleerror2" className={styles.titleError2}>{t('homeList.notFoundTitle2')}</div>
            <p className={styles.descriptionError}>{t('homeList.notFoundDescription')}</p>
          </div> : false
      }
      {
        (!errorData) ?
          (viewContext.mapIsSelected === false && !refreshData) ?
            <AgGridReact className="ag-theme-material"
              ref={gridRef}
              onRowClicked={handleClick}
              onSortChanged={onSortChanged}
              defaultColDef={defaultColDef}
              tooltipShowDelay={1000}
              columnDefs={columnDefs}
              getRowStyle={getRowStyle}
              icons={icons}
              rowModelType={'infinite'}
              cacheBlockSize={ROWS_PER_PAGE}
              onGridReady={onGridReady}
            ></AgGridReact>
            : false
          :
          <div className={styles.dataListError} >
            <Error />
          </div>
      }
      {loadingMore && viewContext.mapIsSelected === false && !errorData && (
        <div className={styles.loadingMore} role="status" aria-live="polite">
          <CircularProgress size={20} aria-label={String(t('homeList.loadingMore'))} />
          <span>{t('homeList.loadingMore')}</span>
        </div>
      )}
    </div >
  );
};

export default grid;
